import React from 'react'
import Dialog from '@mui/material/Dialog'
import type { DialogProps } from '@mui/material/Dialog'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { CHROME_RADIUS, SAFE_AREA_BOTTOM, SAFE_AREA_TOP, TOUCH_MIN } from 'core/theme.tokens'

type AdminDialogProps = DialogProps

/**
 * Long-form phone dialog: full screen with safe-area padding.
 * Short alerts should use a default Dialog (theme inset card) or ConfirmDialog.
 */
export const AdminDialog: React.FC<AdminDialogProps> = ({ children, fullScreen, ...props }) => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const useFullScreen = fullScreen ?? isPhone

  return (
    <Dialog
      fullWidth
      fullScreen={useFullScreen}
      {...props}
      PaperProps={{
        ...props.PaperProps,
        sx: {
          ...(useFullScreen
            ? {
                pt: SAFE_AREA_TOP,
                pb: SAFE_AREA_BOTTOM,
                borderRadius: 0,
                // Column layout so the title pins to the top and actions pin to the bottom.
                display: 'flex',
                flexDirection: 'column',
              }
            : {
                borderRadius: `${CHROME_RADIUS}px`,
              }),
          '& .MuiDialogTitle-root': {
            px: 2.5,
            pt: 2.5,
            ...(useFullScreen
              ? {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }
              : {}),
          },
          '& .MuiDialogContent-root': {
            px: 2.5,
            py: 2,
            ...(useFullScreen ? { flex: 1 } : {}),
          },
          '& .MuiDialogActions-root': {
            px: 2.5,
            pb: 2.5,
            pt: 1.5,
            gap: 1,
            '& .MuiButton-root': { minHeight: TOUCH_MIN },
            ...(useFullScreen
              ? {
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                  // Stack actions; primary (last child) sits on top, full width.
                  flexDirection: 'column-reverse',
                  alignItems: 'stretch',
                  '& .MuiButton-root': { width: '100%', ml: '0 !important' },
                }
              : {}),
          },
          ...(props.PaperProps?.sx as object),
        },
      }}
    >
      {children}
    </Dialog>
  )
}
